/*
 * Dazuma Social Media 1.3.1
 * Metaobject-driven inline social video playback and product reveal behavior.
 * No third-party SDK is loaded until a visitor chooses a video.
 */
(function initializeDazumaSocialMediaMetaobjects() {
  'use strict';

  if (window.DazumaSocialMediaMetaobjects && window.DazumaSocialMediaMetaobjects.version) {
    return;
  }

  const componentVersion = '1.3.1';
  const youtubeIdentifierPattern = /^[A-Za-z0-9_-]{11}$/;
  let fullscreenPlayer = null;
  let fullscreenReturnFocus = null;
  let fullscreenOriginalParent = null;
  let fullscreenOriginalNextSibling = null;
  let fullscreenOwnerCard = null;
  let previousBodyOverflow = null;

  function normalizeHttpUrl(value) {
    if (!value) {
      return null;
    }

    try {
      let normalizedUrl;
      try {
        normalizedUrl = new URL(value);
      } catch (absoluteUrlError) {
        normalizedUrl = new URL(value, window.location.href);
      }

      if (normalizedUrl.protocol !== 'https:' && normalizedUrl.protocol !== 'http:') {
        return null;
      }
      return normalizedUrl;
    } catch (error) {
      return null;
    }
  }

  function normalizeHostName(url) {
    return url.hostname.replace(/^www\./, '').toLowerCase();
  }

  function createYoutubeEmbedSource(videoIdentifier) {
    const queryParameters = new URLSearchParams({
      autoplay: '1',
      playsinline: '1',
      controls: '1',
      fs: '0',
      rel: '0',
      iv_load_policy: '3',
      modestbranding: '1'
    });

    return 'https://www.youtube-nocookie.com/embed/'
      + encodeURIComponent(videoIdentifier)
      + '?'
      + queryParameters.toString();
  }


  function detectDirectVideoProvider(url) {
    const pathName = (url.pathname || '').toLowerCase();
    if (!/\.(mp4|webm|ogg|m4v)$/.test(pathName)) {
      return null;
    }

    return {
      provider: 'native-video',
      source: url.href
    };
  }

  function detectYoutubeProvider(url, hostName) {
    let videoIdentifier = '';

    if (hostName === 'youtu.be') {
      videoIdentifier = url.pathname.split('/').filter(Boolean)[0] || '';
    } else if (hostName.endsWith('youtube.com') || hostName.endsWith('youtube-nocookie.com')) {
      videoIdentifier = url.searchParams.get('v') || '';

      if (!videoIdentifier) {
        const pathMatch = url.pathname.match(/\/(?:shorts|embed|live)\/([A-Za-z0-9_-]{11})(?:\/|$)/);
        if (pathMatch) {
          videoIdentifier = pathMatch[1];
        }
      }
    }

    if (!youtubeIdentifierPattern.test(videoIdentifier)) {
      return null;
    }

    return {
      provider: 'youtube',
      source: createYoutubeEmbedSource(videoIdentifier)
    };
  }

  function detectSocialPostProvider(url, hostName) {
    const isInstagram = hostName === 'instagram.com' || hostName.endsWith('.instagram.com');
    const isTikTok = hostName === 'tiktok.com' || hostName.endsWith('.tiktok.com');
    const isFacebook = hostName === 'facebook.com' || hostName.endsWith('.facebook.com') || hostName === 'fb.watch';

    if (!isInstagram && !isTikTok && !isFacebook) {
      return null;
    }

    return {
      provider: 'unsupported-social',
      source: url.href
    };
  }

  function detectProvider(rawUrl, platformHint) {
    const normalizedUrl = normalizeHttpUrl(rawUrl);
    if (!normalizedUrl) {
      return {
        provider: 'external',
        source: ''
      };
    }

    const hostName = normalizeHostName(normalizedUrl);

    const directVideoProvider = detectDirectVideoProvider(normalizedUrl);
    if (directVideoProvider) {
      return directVideoProvider;
    }

    const youtubeProvider = detectYoutubeProvider(normalizedUrl, hostName);
    if (youtubeProvider) {
      return youtubeProvider;
    }

    const socialPostProvider = detectSocialPostProvider(normalizedUrl, hostName);
    if (socialPostProvider) {
      return socialPostProvider;
    }

    return {
      provider: 'external',
      source: normalizedUrl.href
    };
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function stopPlayer(playerFrame) {
    if (!playerFrame) {
      return;
    }

    playerFrame.replaceChildren();
  }

  function createProviderFrame(detectedProvider, titleText) {
    if (!detectedProvider || detectedProvider.provider === 'external' || detectedProvider.provider === 'unsupported-social' || !detectedProvider.source) {
      return null;
    }

    if (detectedProvider.provider === 'native-video') {
      const video = document.createElement('video');
      video.src = detectedProvider.source;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      video.preload = 'metadata';
      video.disablePictureInPicture = false;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.setAttribute('controlsList', 'nodownload');
      video.setAttribute('aria-label', titleText || 'Video');
      return video;
    }

    const iframe = document.createElement('iframe');
    iframe.src = detectedProvider.source;
    iframe.title = titleText || 'Social video';
    iframe.loading = 'eager';
    iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    return iframe;
  }

  function updateVideoButtonState(videoHub, activeButton) {
    videoHub.querySelectorAll('[data-dz-play-video]').forEach(function updateButton(button) {
      const isActive = button === activeButton;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  function setExpandButtonState(player, isExpanded) {
    if (!player) {
      return;
    }

    const expandButton = player.querySelector('[data-dz-expand-video]');
    if (!expandButton) {
      return;
    }

    expandButton.setAttribute('aria-pressed', isExpanded ? 'true' : 'false');
    const label = isExpanded
      ? (expandButton.dataset.exitLabel || 'Exit enlarged video')
      : (expandButton.dataset.enterLabel || 'Enlarge video');
    expandButton.setAttribute('aria-label', label);
  }

  function exitFullscreenPlayer(restoreFocus) {
    if (!fullscreenPlayer) {
      return;
    }

    const player = fullscreenPlayer;
    const originalParent = fullscreenOriginalParent;
    const originalNextSibling = fullscreenOriginalNextSibling;
    const focusTarget = fullscreenReturnFocus;

    fullscreenPlayer = null;
    fullscreenReturnFocus = null;
    fullscreenOriginalParent = null;
    fullscreenOriginalNextSibling = null;
    fullscreenOwnerCard = null;

    player.classList.remove('is-fullscreen');
    setExpandButtonState(player, false);

    if (originalParent && originalParent.isConnected) {
      if (originalNextSibling && originalNextSibling.parentNode === originalParent) {
        originalParent.insertBefore(player, originalNextSibling);
      } else {
        originalParent.appendChild(player);
      }
    }

    player.style.removeProperty('--dz-social-player-ratio');
    player.style.removeProperty('--dz-social-player-ratio-width');
    player.style.removeProperty('--dz-social-player-ratio-height');
    player.style.removeProperty('--dz-social-radius');

    if (document.body) {
      document.body.classList.remove('dz-social-video-fullscreen-open');
      if (document.body.style.overflow === 'hidden') {
        document.body.style.overflow = previousBodyOverflow || '';
      }
    }
    previousBodyOverflow = null;

    if (restoreFocus && focusTarget && focusTarget.isConnected && typeof focusTarget.focus === 'function') {
      focusTarget.focus({ preventScroll: true });
    }
  }

  function enterFullscreenPlayer(videoCard, expandButton) {
    if (!videoCard || !expandButton || !document.body) {
      return;
    }

    const player = videoCard.querySelector('[data-dz-card-player]');
    if (!player || player.hidden) {
      return;
    }

    if (fullscreenPlayer && fullscreenPlayer !== player) {
      exitFullscreenPlayer(false);
    }

    const videoHub = videoCard.closest('[data-dz-video-hub]');
    const inheritedStyles = window.getComputedStyle(videoHub || videoCard);
    ['--dz-social-player-ratio', '--dz-social-player-ratio-width', '--dz-social-player-ratio-height', '--dz-social-radius'].forEach(function copyFullscreenProperty(propertyName) {
      const propertyValue = inheritedStyles.getPropertyValue(propertyName).trim();
      if (propertyValue) {
        player.style.setProperty(propertyName, propertyValue);
      }
    });

    fullscreenPlayer = player;
    fullscreenReturnFocus = expandButton;
    fullscreenOriginalParent = player.parentNode;
    fullscreenOriginalNextSibling = player.nextSibling;
    fullscreenOwnerCard = videoCard;

    previousBodyOverflow = document.body.style.overflow;
    document.body.appendChild(player);
    player.classList.add('is-fullscreen');
    setExpandButtonState(player, true);
    document.body.style.overflow = 'hidden';
    document.body.classList.add('dz-social-video-fullscreen-open');
  }

  function toggleFullscreenPlayer(videoCard, expandButton) {
    if (!expandButton) {
      return;
    }

    const resolvedVideoCard = videoCard || fullscreenOwnerCard;
    const player = resolvedVideoCard
      ? (resolvedVideoCard.querySelector('[data-dz-card-player]') || fullscreenPlayer)
      : fullscreenPlayer;
    if (!resolvedVideoCard || !player) {
      return;
    }

    if (player.classList.contains('is-fullscreen')) {
      exitFullscreenPlayer(true);
    } else {
      enterFullscreenPlayer(resolvedVideoCard, expandButton);
    }
  }

  function closeCardPlayer(videoCard, restoreFocus) {
    if (!videoCard) {
      return;
    }

    const player = (fullscreenOwnerCard === videoCard && fullscreenPlayer)
      ? fullscreenPlayer
      : videoCard.querySelector('[data-dz-card-player]');
    const playerFrame = player ? player.querySelector('[data-dz-card-player-frame]') : null;
    const playerMedia = player ? player.querySelector('[data-dz-card-player-media]') : null;
    const playButton = videoCard.querySelector('[data-dz-play-video]');

    if (player && player.classList.contains('is-fullscreen')) {
      exitFullscreenPlayer(false);
    }

    stopPlayer(playerMedia);

    if (player) {
      player.hidden = true;
    }
    if (playButton) {
      playButton.hidden = false;
      playButton.classList.remove('is-active');
      playButton.setAttribute('aria-pressed', 'false');
    }

    videoCard.classList.remove('is-playing');

    if (restoreFocus && playButton && typeof playButton.focus === 'function') {
      playButton.focus({ preventScroll: true });
    }
  }

  function closeOtherCardPlayers(videoHub, activeCard) {
    videoHub.querySelectorAll('[data-dz-video-card]').forEach(function closeOtherCard(videoCard) {
      if (videoCard !== activeCard) {
        closeCardPlayer(videoCard, false);
      }
    });
  }

  function openCardPlayer(videoHub, triggerButton) {
    const videoCard = triggerButton ? triggerButton.closest('[data-dz-video-card]') : null;
    if (!videoHub || !videoCard || !triggerButton) {
      return;
    }

    const player = (fullscreenOwnerCard === videoCard && fullscreenPlayer)
      ? fullscreenPlayer
      : videoCard.querySelector('[data-dz-card-player]');
    const playerMedia = player ? player.querySelector('[data-dz-card-player-media]') : null;

    if (!player || !playerMedia) {
      return;
    }

    closeOtherCardPlayers(videoHub, videoCard);
    stopPlayer(playerMedia);

    const playbackUrl = triggerButton.dataset.playbackUrl || '';
    const platformHint = triggerButton.dataset.platform || videoHub.dataset.platform || '';
    const titleText = triggerButton.dataset.title || '';
    const detectedProvider = detectProvider(playbackUrl, platformHint);
    const providerFrame = createProviderFrame(detectedProvider, titleText);

    if (!providerFrame) {
      videoCard.dataset.playbackState = 'unsupported';
      triggerButton.setAttribute('aria-pressed', 'false');
      return;
    }

    delete videoCard.dataset.playbackState;
    player.dataset.provider = detectedProvider.provider || 'external';
    playerMedia.appendChild(providerFrame);

    if (providerFrame.tagName === 'VIDEO' && typeof providerFrame.play === 'function') {
      const playbackPromise = providerFrame.play();
      if (playbackPromise && typeof playbackPromise.catch === 'function') {
        playbackPromise.catch(function ignoreAutoplayRejection() {
          // The visitor can start playback with the native control if the browser blocks autoplay.
        });
      }
    }
    triggerButton.hidden = true;
    triggerButton.classList.add('is-active');
    triggerButton.setAttribute('aria-pressed', 'true');
    player.hidden = false;
    videoCard.classList.add('is-playing');

    videoCard.scrollIntoView({
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      block: 'nearest',
      inline: 'center'
    });

    window.requestAnimationFrame(function refreshRailAfterPlaybackChange() {
      updateRailButtons(videoHub);
    });
  }

  function updateRailButtons(videoHub) {
    if (!videoHub) {
      return;
    }

    const videoTrack = videoHub.querySelector('[data-dz-video-track]');
    if (!videoTrack) {
      return;
    }

    const previousButton = videoHub.querySelector('[data-dz-rail="previous"]');
    const nextButton = videoHub.querySelector('[data-dz-rail="next"]');
    const maximumScrollLeft = Math.max(0, videoTrack.scrollWidth - videoTrack.clientWidth);
    const currentScrollLeft = Math.max(0, videoTrack.scrollLeft);
    const hasOverflow = maximumScrollLeft > 2;

    if (previousButton) {
      previousButton.hidden = !hasOverflow;
      previousButton.disabled = !hasOverflow || currentScrollLeft <= 2;
    }

    if (nextButton) {
      nextButton.hidden = !hasOverflow;
      nextButton.disabled = !hasOverflow || currentScrollLeft >= maximumScrollLeft - 2;
    }
  }

  function initializeVideoHub(videoHub) {
    if (!videoHub || videoHub.dataset.dzInitialized === 'true') {
      return;
    }

    const videoTrack = videoHub.querySelector('[data-dz-video-track]');
    if (!videoTrack) {
      return;
    }

    videoHub.dataset.dzInitialized = 'true';
    videoTrack.addEventListener('scroll', function handleVideoTrackScroll() {
      updateRailButtons(videoHub);
    }, { passive: true });

    updateRailButtons(videoHub);
  }

  function initializeAllVideoHubs(rootElement) {
    const root = rootElement && rootElement.querySelectorAll ? rootElement : document;
    root.querySelectorAll('[data-dz-video-hub]').forEach(function initializeHub(videoHub) {
      initializeVideoHub(videoHub);
    });
  }

  document.addEventListener('click', function handleDocumentClick(event) {
    const eventTarget = event.target;
    if (!eventTarget || typeof eventTarget.closest !== 'function') {
      return;
    }

    const playButton = eventTarget.closest('[data-dz-play-video]');
    if (playButton) {
      const videoHub = playButton.closest('[data-dz-video-hub]');
      if (videoHub) {
        openCardPlayer(videoHub, playButton);
      }
      return;
    }

    const expandVideoButton = eventTarget.closest('[data-dz-expand-video]');
    if (expandVideoButton) {
      const videoCard = expandVideoButton.closest('[data-dz-video-card]') || fullscreenOwnerCard;
      toggleFullscreenPlayer(videoCard, expandVideoButton);
      return;
    }

    const closeVideoButton = eventTarget.closest('[data-dz-close-video]');
    if (closeVideoButton) {
      const videoCard = closeVideoButton.closest('[data-dz-video-card]') || fullscreenOwnerCard;
      const videoHub = videoCard ? videoCard.closest('[data-dz-video-hub]') : null;
      closeCardPlayer(videoCard, true);
      if (videoHub) {
        window.requestAnimationFrame(function refreshRailAfterClose() {
          updateRailButtons(videoHub);
        });
      }
      return;
    }

    const railButton = eventTarget.closest('[data-dz-rail]');
    if (railButton) {
      const videoHub = railButton.closest('[data-dz-video-hub]');
      const videoTrack = videoHub ? videoHub.querySelector('[data-dz-video-track]') : null;
      if (!videoTrack) {
        return;
      }

      const directionMultiplier = railButton.dataset.dzRail === 'previous' ? -1 : 1;
      videoTrack.scrollBy({
        left: videoTrack.clientWidth * 0.9 * directionMultiplier,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
      return;
    }

    const loadProductsButton = eventTarget.closest('[data-dz-load-products]');
    if (loadProductsButton) {
      const productsSection = loadProductsButton.closest('.dz-social__products');
      if (!productsSection) {
        return;
      }

      productsSection.querySelectorAll('[data-dz-product-card][hidden]').forEach(function revealProductCard(productCard) {
        productCard.hidden = false;
      });
      loadProductsButton.setAttribute('aria-expanded', 'true');
      loadProductsButton.hidden = true;
    }
  });

  document.addEventListener('keydown', function handleVideoKeyboard(event) {
    if (event.key === 'Escape' && fullscreenPlayer) {
      event.preventDefault();
      exitFullscreenPlayer(true);
    }
  });

  document.addEventListener('shopify:section:load', function handleSectionLoad(event) {
    initializeAllVideoHubs(event.target);
  });

  document.addEventListener('shopify:section:unload', function handleSectionUnload(event) {
    if (!event.target || !event.target.querySelectorAll) {
      return;
    }
    if (fullscreenPlayer && fullscreenOwnerCard && event.target.contains(fullscreenOwnerCard)) {
      exitFullscreenPlayer(false);
    }
    event.target.querySelectorAll('[data-dz-card-player-media]').forEach(function releaseSectionPlayer(playerMedia) {
      stopPlayer(playerMedia);
    });
  });

  window.addEventListener('resize', function handleWindowResize() {
    document.querySelectorAll('[data-dz-video-hub]').forEach(function refreshRail(videoHub) {
      updateRailButtons(videoHub);
    });
  }, { passive: true });

  window.addEventListener('pagehide', function handlePageHide() {
    if (fullscreenPlayer) {
      exitFullscreenPlayer(false);
    }
    document.querySelectorAll('[data-dz-card-player-media]').forEach(function releasePlayer(playerMedia) {
      stopPlayer(playerMedia);
    });
  });

  window.DazumaSocialMediaMetaobjects = {
    version: componentVersion,
    detectProvider: detectProvider,
    initialize: initializeAllVideoHubs
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function handleDomReady() {
      initializeAllVideoHubs(document);
    }, { once: true });
  } else {
    initializeAllVideoHubs(document);
  }
})();
